import { fmtNum, roDateTime, type PriceQuote, t } from '@procumi/shared';
import { useState } from 'react';

/**
 * Evoluția prețului unui produs, în timp.
 *
 * **O singură serie.** Linia unește cel mai mic preț primit la fiecare cerere —
 * „cât ai fi putut plăti". Punctele estompate din jur sunt toate ofertele, deci
 * se vede și cât de împrăștiate au fost. Nu colorăm pe furnizor: brandul are
 * ardezie pentru structură, albastru pentru acțiune și verde doar pentru bani
 * economisiți, iar o paletă categorială inventată aici ar fi al doilea sistem
 * de culoare. Identitatea furnizorului vine din tooltip și din tabelul de sub
 * grafic, niciodată din culoare singură.
 *
 * Verdele marchează cel mai mic preț din istoric — exact regula brandului.
 * Perechea albastru/verde a trecut validatorul de daltonism pe ambele teme
 * (ΔE 18,2 protan pe lumină, 15,6 deutan pe întuneric).
 */

const W = 680;
const H = 230;
const PAD = { top: 18, right: 22, bottom: 30, left: 58 };

interface Point {
  q: PriceQuote;
  x: number;
  y: number;
}

export function PriceChart({ quotes, unit }: { quotes: PriceQuote[]; unit: string }) {
  const [hover, setHover] = useState<Point | null>(null);

  if (quotes.length < 2) return null;

  const times = quotes.map((q) => new Date(q.at).getTime());
  const prices = quotes.map((q) => q.price);
  const t0 = Math.min(...times);
  const t1 = Math.max(...times);
  let lo = Math.min(...prices);
  let hi = Math.max(...prices);
  if (hi === lo) {
    // toate prețurile egale: lăsăm aer, ca linia să nu stea lipită de margine
    lo = lo * 0.95;
    hi = hi * 1.05;
  } else {
    const aer = (hi - lo) * 0.15;
    lo -= aer;
    hi += aer;
  }

  const x = (t: number): number =>
    t1 === t0
      ? (PAD.left + W - PAD.right) / 2
      : PAD.left + ((t - t0) / (t1 - t0)) * (W - PAD.left - PAD.right);
  const y = (p: number): number => H - PAD.bottom - ((p - lo) / (hi - lo)) * (H - PAD.top - PAD.bottom);

  const points: Point[] = quotes.map((q) => ({ q, x: x(new Date(q.at).getTime()), y: y(q.price) }));

  // Linia trece prin cel mai bun preț al fiecărei cereri: o cerere = o decizie.
  const perRfq = new Map<string, PriceQuote>();
  for (const q of quotes) {
    const best = perRfq.get(q.rfqId);
    if (!best || q.price < best.price) perRfq.set(q.rfqId, q);
  }
  const line = [...perRfq.values()]
    .map((q) => ({ q, x: x(new Date(q.at).getTime()), y: y(q.price) }))
    .sort((a, b) => a.x - b.x);

  const cheapest = quotes.reduce((min, q) => (q.price < min.price ? q : min), quotes[0]!);
  const latest = line[line.length - 1]!;

  // Trei repere pe verticală: minimul, mijlocul, maximul intervalului desenat.
  const ticks = [lo, (lo + hi) / 2, hi];

  return (
    <div className="chart-wrap">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="chart"
        role="img"
        aria-label={t(
          'Evoluția prețului: {0} oferte, între {1} și {2} lei pe {3}. Valorile exacte sunt în tabelul de mai jos.',
          quotes.length,
          fmtNum(Math.min(...prices)),
          fmtNum(Math.max(...prices)),
          unit,
        )}
      >
        {ticks.map((t, i) => (
          <g key={i}>
            <line x1={PAD.left} x2={W - PAD.right} y1={y(t)} y2={y(t)} className="grid" />
            <text x={PAD.left - 10} y={y(t) + 4} className="tick" textAnchor="end">
              {fmtNum(Math.round(t * 100) / 100)}
            </text>
          </g>
        ))}
        <text x={PAD.left} y={H - 8} className="tick">
          {roDateTime(t0)}
        </text>
        <text x={W - PAD.right} y={H - 8} className="tick" textAnchor="end">
          {roDateTime(t1)}
        </text>

        {line.length > 1 ? (
          <polyline className="trend" points={line.map((p) => `${p.x},${p.y}`).join(' ')} />
        ) : null}

        {/* toate ofertele: împrăștierea prețurilor la fiecare cerere */}
        {points.map((p, i) => (
          <circle
            key={i}
            cx={p.x}
            cy={p.y}
            r={5}
            className={`dot${p.q === cheapest ? ' best' : ''}`}
            onMouseEnter={() => setHover(p)}
            onMouseLeave={() => setHover(null)}
          />
        ))}

        {/* eticheta directă: cel mai bun preț și ultimul, nu toate */}
        <text
          x={x(new Date(cheapest.at).getTime())}
          y={y(cheapest.price) - 12}
          className="mark best"
          textAnchor="middle"
        >
          {t('cel mai bun {0}', fmtNum(cheapest.price))}
        </text>
        {latest.q !== cheapest ? (
          <text x={latest.x} y={latest.y - 12} className="mark" textAnchor="end">
            {fmtNum(latest.q.price)}
          </text>
        ) : null}
      </svg>

      {hover ? (
        <div
          className="chart-tip"
          style={{ left: `${(hover.x / W) * 100}%`, top: `${(hover.y / H) * 100}%` }}
        >
          <b>{t('{0} lei/{1}', fmtNum(hover.q.price), hover.q.unit)}</b>
          <br />
          {hover.q.supplier.name}
          <br />
          <span className="muted">
            {roDateTime(hover.q.at)} · {hover.q.rfqTitle}
          </span>
        </div>
      ) : null}
    </div>
  );
}
